import { IParsedPivotConfig, ICell, IField } from "../types";
import { getUnique, methods } from "./groupfuncs";

import { uid } from "@dhx/ts-common/core";
import locale from "../locales/en";
import { IGrid } from "@dhx/ts-grid";
import { TreeCollection } from "@dhx/ts-data";
import { dateToStr } from "../helpers";

interface IParentInfo {
	id: string;
	chain: string[];
	repeatLabels?: boolean;
}

// [dirty]
let pivotConfig: IParsedPivotConfig;

export function getValue(obj: any, key: string, fallback: any = "") {
	return obj ? obj[key] || obj : fallback;
}

function setRowsLabels(
	rows: IField[],
	uniqueRows: any[],
	level: number,
	rowName: string,
	parent: IParentInfo
) {
	const firstColumns = {} as any;
	firstColumns.id = uid();

	if (pivotConfig.layout.gridMode === "tree") {
		if (level > 0 && parent && parent.id) {
			firstColumns.parent = parent.id;
		}
		let text = getValue(rowName, "label");
		if (rows[level].template) {
			text = rows[level].template(text, rows[level]);
		}
		let hasDate = 0;
		rows.forEach(row => (hasDate = row.type === "date" && !row.group ? hasDate++ : hasDate));
		if (rows[level].type === "date" && !rows[level].group && hasDate !== rows.length) {
			text = dateToStr(text, rows[level].format || "%M %d %Y");
		}

		firstColumns[0] = text;
	} else {
		rows.forEach((_row, i) => {
			let text = (pivotConfig.layout.repeatRowsHeaders && parent.chain && parent.chain[i]) || "";

			if (i === level) {
				text = getValue(rowName, "label");
				if (rows[level].template) {
					text = rows[level].template(text, rows[level]);
				}
			}

			firstColumns[i] = text;
			if (rows.length > 1) {
				firstColumns.$level = level ? level - 1 : level - 0.8;
			} else {
				firstColumns.$level = level;
			}
		});
	}

	uniqueRows.push(firstColumns);
	return firstColumns.id;
}

function getRows(
	columns: ICell[][],
	uniqueRows: any[],
	rows: any,
	level = 0,
	parent?: IParentInfo
): ICell[][][] {
	const rowName = rows[level].id;
	const dir = rows[level].sortDir || "asc";
	const unqRowNames = getUnique(columns, rowName, dir);
	parent = parent || { id: null, chain: null };

	const result = [];
	let id: string;
	for (let i = 0; i < unqRowNames.length; i++) {
		const unqRowName = getValue(unqRowNames[i], "id");
		// build first columns
		id = setRowsLabels(rows, uniqueRows, level, unqRowNames[i], parent);

		const row = [];
		for (let k = 0; k < columns.length; k++) {
			const cell = [];
			for (let j = 0; j < columns[k].length; j++) {
				if (getValue(columns[k][j][rowName], "id") === unqRowName) {
					cell.push(columns[k][j]);
				}
			}
			row.push(cell);
		}
		result.push(row);

		if (level < rows.length - 1) {
			if (pivotConfig.layout.gridMode === "tree") {
				result.push(...getRows(row, uniqueRows, rows, level + 1, { id, chain: [] }));
			} else {
				parent.id = id;
				parent.chain = parent.chain || [];
				parent.chain.push(unqRowName);

				result.push(...getRows(row, uniqueRows, rows, level + 1, parent));

				parent.chain.pop();
			}
		}
	}
	return result;
}

function getGroupedData(config: IParsedPivotConfig, columns: ICell[][], _data: any[]) {
	const uniqueRows = [];
	const configRows = config.fields.rows;
	const rows = getRows(columns, uniqueRows, configRows, 0);
	const fields = config.fields.values;

	if (fields) {
		for (let i = 0; i < rows.length; i++) {
			let row: string[];
			if (pivotConfig.layout.gridMode === "tree") {
				row = [uniqueRows[i][0]];
			} else {
				row = config.fields.rows.map((_row: any, ind: number) => uniqueRows[i][ind]);
			}

			for (let k = 0; k < rows[i].length; k++) {
				for (let j = 0; j < fields.length; j++) {
					row.push(methods(rows[i][k], fields[j].id, fields[j].method));
				}
			}
			uniqueRows[i] = { ...row, ...uniqueRows[i] };
		}
		return uniqueRows;
	}
}

function getColumns(data: any[], columns: IField[]): ICell[][] {
	const groups = {};
	for (let i = 0; i < data.length; i++) {
		data[i].$keyArr = columns.map(col => getValue(data[i][col.id], "id"));
		const group = data[i].$keyArr.join(".");
		groups[group] = groups[group] || [];
		groups[group].push(data[i]);
	}

	return Object.keys(groups)
		.map(group => groups[group])
		.sort((a: ICell, b: ICell) => {
			for (let i = 0; i < a[0].$keyArr.length; i++) {
				const an = a[0].$keyArr[i];
				const bn = b[0].$keyArr[i];

				let nn = isNaN(an) ? an.localeCompare(bn) : an - bn;
				const dir = columns[i].sortDir || "asc";
				nn = dir === "asc" ? nn : -nn;
				if (nn) {
					return nn;
				}
			}
		});
}

function getHeaderLabels(columns: ICell[][], config: IField[]) {
	return columns.map(col =>
		config.map(f => {
			const field = f.id;
			return `${getValue(col[0][field], "label")}`;
		})
	);
}

function getHeader(config: IParsedPivotConfig, columns: ICell[][]) {
	if (!config.fields.values) {
		return;
	}

	let colId = 0;
	let result: any[];
	const _header = config.fields.columns;
	const fields = config.fields.values;
	const footer = config.layout.footer ? [{ text: locale.total }] : null;
	const width = config.layout.columnsWidth ? config.layout.columnsWidth : null;

	if (pivotConfig.layout.gridMode === "tree") {
		const header = [];
		for (let i = 0; i <= config.fields.columns.length; i++) {
			header.push({ text: "" });
		}

		let format;
		let hasDate = 0;
		const rows = config.fields.rows;
		rows.forEach(row => {
			if (row.type === "date" && !row.group) {
				hasDate++;
				if (row.format) format = row.format;
			}
		});

		const type = hasDate === rows.length ? "date" : "string";
		result = [
			{
				id: (colId++).toString(),
				header,
				footer,
				width,
				type,
				format,
			},
		];
	} else {
		result = [];
		config.fields.rows.forEach(row => {
			const header = [
				{
					text: row.label || row.id,
					rowspan: config.fields.columns.length + 1,
				},
			];
			const type = row.type && !row.group ? row.type : "string";
			const format = row.format && row.type === "date" && !row.group ? row.format : null;
			result.push({
				header,
				footer,
				id: (colId++).toString(),
				width,
				type,
				format,
			});
		});
	}

	const prevKeys = [];
	const colspans = [];
	const spanCells = [];
	columns = getHeaderLabels(columns, _header) as any;
	for (let i = 0; i < columns.length; i++) {
		for (const field of fields) {
			const column = [];
			for (let level = 0; level < columns[i].length; level++) {
				const key = columns[i][level].id || columns[i][level];
				prevKeys[level] = prevKeys[level] || [];
				colspans[level] = colspans[level] || 1;
				spanCells[level] = spanCells[level] || {};
				if (prevKeys[level] !== key) {
					prevKeys[level] = key;
					spanCells[level] = {
						text: key,
						colspan: colspans[level],
					};
					if (_header[level].template) {
						spanCells[level].text = _header[level].template(key, _header[level]);
					}
					column.push({ ...spanCells[level], align: "right" });
				} else {
					spanCells[level].colspan++;
					column.push({ text: null });
				}
			}

			column.push({
				text: `${field.label || field.id} (${locale[field.method]})`,
				align: "right",
			});

			const col = {
				header: column,
				id: (colId++).toString(),
				mark: config.mark,
				align: "right",
				template: (v, r, c) => {
					if (v === null) {
						return "";
					}
					if (config.customFormat) {
						v = config.customFormat(v, col.method);
					}
					if (field.cellTemplate) {
						return field.cellTemplate(v, r, c);
					}
					return v;
				},
				method: field.method,
				width,
			} as any;

			if (config.layout && config.layout.footer) {
				col.footer = [{ content: field.method, align: "right" }];
			}
			result.push(col);
		}
	}

	return result;
}

function normalizeFooter(grid: IGrid, cols: any[]) {
	const content = grid.content;
	const datacollection = grid.data as TreeCollection;
	const data = datacollection.map(i => i);
	for (const col of cols) {
		const id = col.id;
		const columnData = data.reduce((items: any[], item) => {
			if (item[id] !== undefined && item[id] !== "" && !isNaN(item[id])) {
				items.push(parseFloat(item[id]));
			}
			return items;
		}, []);
		const roots = data.reduce((total, item) => {
			if (item.$level === 0) {
				if (item[id] !== undefined && item[id] !== "" && !isNaN(item[id])) {
					total.push(parseFloat(item[id]) || 0);
				} else {
					let value = 0;
					datacollection.eachChild(item.id, cell => {
						if (!datacollection.haveItems(cell.id)) {
							value += parseFloat(cell[id]);
						}
					});
					total.push(value);
				}
			}
			return total;
		}, []);
		if (content[col.method] && col.template) {
			const val = content[col.method].calculate(columnData, roots);
			const htmlVal = col.template(val);
			col.footer = [{ text: htmlVal, align: col.align }];
		}
	}
}

export function getGridData(config: IParsedPivotConfig, data: any[], grid: IGrid) {
	pivotConfig = config;
	const columns = getColumns(data, config.fields.columns);

	const headers = getHeader(config, columns);
	const groupedData = getGroupedData(config, columns, data);
	if (config.layout && config.layout.footer && config.customFormat) {
		grid.data.parse(groupedData);
		normalizeFooter(grid, headers);
	}
	return {
		headers,
		groupedData,
	};
}
